<template>
  <div class="instagram-analytics-public-summary">
    <el-row class="mb-x2" :gutter="20">
      <el-col :span="8">
        <AnalyticsTotal
          metric="応募数"
          :value="entryCount"
          tooltip="キャンペーンへの応募数です。この数値には重複応募も含まれます。"
        ></AnalyticsTotal>
      </el-col>
      <el-col :span="8">
        <AnalyticsTotal
          metric="応募者数"
          :value="applicantCount"
          tooltip="キャンペーンへ応募したアカウントの数です。"
        ></AnalyticsTotal>
      </el-col>
      <el-col :span="8">
        <AnalyticsTotal
          metric="新規応募者数"
          :value="newApplicantCount"
          tooltip="同一開催アカウントで過去に実施したキャンペーンに応募した実績のない応募者の数です。"
        ></AnalyticsTotal>
      </el-col>
    </el-row>
    <el-row class="mb-x3" :gutter="20">
      <el-col :span="8">
        <AnalyticsTotal
          metric="フォロワー増減数"
          :value="followerUpDown"
          :isUpDown="true"
          tooltip="キャンペーン開催アカウントのフォロワー数の増減です。"
        ></AnalyticsTotal>
      </el-col>
      <el-col :span="8">
        <AnalyticsTotal metric="当選数" :value="winnerCount"></AnalyticsTotal>
      </el-col>
    </el-row>
  </div>
</template>

<script lang="ts">
import { storeToRefs } from 'pinia'
import { computed, defineComponent } from 'vue'

import AnalyticsTotal from '@/client/components/_atoms/analytics_total'
import { useInstagramInstantwinAnalyticsPublicStore } from '@/client/features/instagram_instantwin_analytics_public/hooks/instagramInstantwinAnalyticsPublicStore'

export default defineComponent({
  components: { AnalyticsTotal },
  setup() {
    const store = useInstagramInstantwinAnalyticsPublicStore()
    const { campaignAnalytics } = storeToRefs(store)

    const columnChartData = computed(() => {
      return campaignAnalytics.value.slice(0, campaignAnalytics.value.length - 1)
    })

    const addition = (numbers: number[] = []): number | null => {
      const values = numbers.filter(v => v !== null)

      if (!values.length) {
        return null
      }

      return numbers.map(v => toNumber(v)).reduce((a, c) => a + c, 0)
    }

    const getFirstValueExceptNull = (analytics: any[], value: string) => {
      const data = analytics.find(analytic => analytic[value] !== null)

      if (!data) {
        return 0
      }

      return data[value]
    }

    const getLastValue = (analytics: any[], value: string) => {
      const data = analytics.filter(analytic => analytic[value] !== null)

      if (data.length === 0) {
        return null
      }

      return data[data.length - 1][value]
    }

    const getMetricArray = (insights: any[], metric: string): any[] => {
      return insights.map(insight => getMetricValue(insight, metric))
    }

    const getMetricValue = (insight: any, metric: string): any | null => {
      return isNaN(insight[metric]) ? null : insight[metric]
    }

    const getTotal = (data: any, metric: string): number | null => {
      return addition(getMetricArray(data, metric))
    }

    const toNumber = (value: any): number => {
      return !isNaN(value) ? value : 0
    }

    const entryCount = computed(() => getTotal(columnChartData.value, 'entry_count'))
    const applicantCount = computed(() => getTotal(columnChartData.value, 'applicant_count'))
    const newApplicantCount = computed(() => getTotal(columnChartData.value, 'new_applicant_count'))
    const winnerCount = computed(() => getTotal(columnChartData.value, 'winner_count'))
    const followerUpDown = computed(() => {
      const firstFollowerCount = getFirstValueExceptNull(campaignAnalytics.value, 'follower_count')

      return getLastValue(campaignAnalytics.value, 'follower_count') - firstFollowerCount
    })

    return {
      entryCount,
      applicantCount,
      newApplicantCount,
      winnerCount,
      followerUpDown
    }
  }
})
</script>
